import * as React from "react";
import { cn } from "@/lib/utils";
import { formatInputValue, parseIndianNumber, calculateCursorPosition } from "@/lib/formatters";

interface NumericInputProps extends Omit<React.ComponentProps<"input">, "onChange" | "value"> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  allowDecimal?: boolean;
  decimalPlaces?: number;
}

const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>(
  ({ className, value, onChange, min, max, allowDecimal = false, decimalPlaces = 2, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [displayValue, setDisplayValue] = React.useState(() => 
      value === 0 ? '' : formatInputValue(value.toString())
    );
    const [isFocused, setIsFocused] = React.useState(false);

    // Sync display value when external value changes (and input is not focused)
    React.useEffect(() => {
      if (!isFocused) {
        setDisplayValue(value === 0 ? '' : formatInputValue(value.toString()));
      }
    }, [value, isFocused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const cursorPosition = input.selectionStart || 0;
      const oldValue = displayValue;
      let rawValue = e.target.value;

      // Remove commas for processing
      let cleanValue = rawValue.replace(/,/g, '');

      // Handle decimal restriction
      if (!allowDecimal) {
        cleanValue = cleanValue.replace(/\./g, '');
      } else if (decimalPlaces !== undefined) {
        // Limit decimal places
        const parts = cleanValue.split('.');
        if (parts[1] && parts[1].length > decimalPlaces) {
          parts[1] = parts[1].slice(0, decimalPlaces);
          cleanValue = parts.join('.');
        }
      }

      // Remove non-numeric characters (except decimal and minus)
      cleanValue = cleanValue.replace(/[^0-9.-]/g, '');

      // Handle leading zeros - remove them when typing
      if (cleanValue.length > 1 && cleanValue.startsWith('0') && cleanValue[1] !== '.') {
        cleanValue = cleanValue.replace(/^0+/, '') || '0';
      }

      // Handle negative with leading zeros
      if (cleanValue.startsWith('-0') && cleanValue.length > 2 && cleanValue[2] !== '.') {
        cleanValue = '-' + cleanValue.slice(1).replace(/^0+/, '');
      }

      // Ensure only one decimal point
      const decimalCount = (cleanValue.match(/\./g) || []).length;
      if (decimalCount > 1) {
        const firstDecimal = cleanValue.indexOf('.');
        cleanValue = cleanValue.slice(0, firstDecimal + 1) + cleanValue.slice(firstDecimal + 1).replace(/\./g, '');
      }

      // Ensure minus only at start
      if (cleanValue.includes('-')) {
        cleanValue = (cleanValue.startsWith('-') ? '-' : '') + cleanValue.replace(/-/g, '');
      }

      // Format with Indian commas
      const formattedValue = cleanValue === '' || cleanValue === '-' ? cleanValue : formatInputValue(cleanValue);

      // Parse numeric value
      let numericValue = parseIndianNumber(formattedValue);

      // Apply min/max constraints only on blur, not during typing
      // This allows users to type freely

      setDisplayValue(formattedValue);
      onChange(numericValue);

      // Calculate and set new cursor position
      requestAnimationFrame(() => {
        if (inputRef.current) {
          const newCursor = calculateCursorPosition(oldValue, formattedValue, cursorPosition);
          inputRef.current.setSelectionRange(newCursor, newCursor);
        }
      });
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // If value is 0, show empty field for easier typing
      if (value === 0) {
        setDisplayValue('');
      }
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      // On blur, if empty, set to 0
      if (displayValue === '' || displayValue === '-') {
        onChange(0);
        setDisplayValue('');
      } else {
        // Apply min/max constraints on blur
        let numericValue = parseIndianNumber(displayValue);
        if (min !== undefined && numericValue < min) {
          numericValue = min;
        }
        if (max !== undefined && numericValue > max) {
          numericValue = max;
        }
        onChange(numericValue);
        // Re-format the value properly
        const formatted = formatInputValue(numericValue.toString());
        setDisplayValue(formatted);
      }
      props.onBlur?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter, decimal point (if allowed)
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (allowDecimal) {
        allowedKeys.push('.');
      }
      
      if (
        allowedKeys.includes(e.key) ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) ||
        (e.metaKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) ||
        // Allow numbers
        /^\d$/.test(e.key) ||
        // Allow minus at start
        (e.key === '-' && (e.currentTarget.selectionStart === 0 || displayValue === ''))
      ) {
        return;
      }
      
      e.preventDefault();
    };

    // Merge refs
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    return (
      <input
        type="text"
        inputMode="numeric"
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        aria-label={props["aria-label"] || props.placeholder || props.name || "Numeric input"}
        ref={inputRef}
        value={displayValue}
        placeholder="0"
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        {...props}
      />
    );
  }
);

NumericInput.displayName = "NumericInput";

export { NumericInput };
